<template>
  <div class="processing-batch-list" :class="{ 'processing-batch-list--embedded': embedded }">
    <breadcrumb v-if="!embedded" :items="breadcrumbs"></breadcrumb>

    <component :is="embedded ? 'div' : 'v-container'" :fluid="!embedded">
      <div v-if="!embedded" class="d-flex mb-4 flex-wrap">
        <h2 class="font-weight-bolder">{{ $t("memberDataSection.processingBatch") }}</h2>
        <v-spacer></v-spacer>
        <v-btn
          v-if="getModulePermittedActions.post"
          outlined
          color="primary"
          class="mr-2 mb-2 text-uppercase"
          @click="openBulkImport"
        >
          {{ $t("memberDataSection.processingBatchBulkImport.bulkImport") }}
        </v-btn>
        <v-btn
          v-if="getModulePermittedActions.post"
          color="primary"
          dark
          class="mr-2 mb-2 text-uppercase"
          @click="openAdd"
        >
          {{ $t("memberDataSection.addProcessingBatch") }}
        </v-btn>
      </div>

      <component :is="embedded ? 'div' : 'v-card'" elevation="0" :class="embedded ? '' : 'px-5'">
        <component :is="embedded ? 'div' : 'v-card-text'">
          <v-data-table
            ref="listTable"
            :headers="tableHeaders"
            :items="records"
            :items-per-page="options.itemsPerPage"
            :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
            item-key="id"
            hide-default-footer
            :loading="loading"
            :options.sync="options"
            :server-items-length="totalRecords"
            :loading-text="$t('loadingData')"
            class="processing-batch-list-table"
          >
            <template v-slot:header.greenCoffeeOut>
              <span class="processing-batch-list-table__two-line-header">
                {{ parchmentHeaderLines.first }}<br />{{ parchmentHeaderLines.second }}
              </span>
            </template>

            <template v-slot:item.batchId="{ item }">
              {{ item.batchCode || "—" }}
            </template>

            <template v-slot:item.lots="{ item }">
              <div v-for="order in item.buyingStationOrder" :key="order.id">
                {{ order.orderCode }}
              </div>
              <span v-if="!item.buyingStationOrder || !item.buyingStationOrder.length">—</span>
            </template>

            <template v-slot:item.greenCoffeeOut="{ item }">
              <div>{{ item.parchmentOut != null ? `${item.parchmentOut} kg` : "N/A" }}</div>
              <div v-if="item.batchRating" class="mt-1">{{ item.batchRating }}</div>
            </template>

            <template v-slot:item.startEndDate="{ item }">
              {{ formatDate(item.startDate) }}<br />
              {{ formatDate(item.endDate) }}
            </template>

            <template v-slot:item.status="{ item }">
              <v-btn
                v-if="item.parchmentOut != null"
                x-small
                rounded
                min-width="80"
                depressed
                class="green-button"
                height="28"
              >
                {{ $t("memberDataSection.completed") }}
              </v-btn>
              <v-btn
                v-else
                x-small
                rounded
                min-width="80"
                depressed
                class="orange-button"
                height="28"
              >
                {{ $t("memberDataSection.inProcess") }}
              </v-btn>
            </template>

            <template v-slot:item.action="{ item }">
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="viewRecord(item)">
                    <v-list-item-title>{{ $t("memberDataSection.view") }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    :disabled="!getModulePermittedActions.put"
                    @click="editRecord(item)"
                  >
                    <v-list-item-title>{{ $t("memberDataSection.edit") }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    :disabled="!getModulePermittedActions.delete"
                    @click="deleteRecord(item)"
                  >
                    <v-list-item-title class="red--text">{{ $t("memberDataSection.delete") }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>

            <template v-slot:top>
              <div class="d-flex mb-4 flex-wrap align-center" :class="{ 'mt-2': embedded }">
                <div class="ml-3" style="width: 280px;">
                  <v-text-field
                    v-model="search"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    :placeholder="$t('memberDataSection.search')"
                    hide-details
                    @input="onSearch"
                  ></v-text-field>
                </div>
                <v-spacer></v-spacer>
                <div class="d-flex align-center mb-3">
                  <div class="py-1 title">
                    {{ fromRecord }} - {{ toRecord }} {{ $t("of") }} {{ totalRecords }}
                  </div>
                  <v-btn
                    class="mx-2"
                    fab
                    small
                    outlined
                    color="primary"
                    :disabled="options.page <= 1"
                    @click="pageChange(false)"
                  >
                    <v-icon dark>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    class="mx-2"
                    fab
                    small
                    outlined
                    color="primary"
                    :disabled="options.page >= totalPages"
                    @click="pageChange(true)"
                  >
                    <v-icon dark>mdi-chevron-right</v-icon>
                  </v-btn>
                  <v-menu
                    v-model="customizeMenu"
                    :close-on-content-click="false"
                    :nudge-width="200"
                    left
                    :min-width="417"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs" v-on="on">
                        <v-icon dark>mdi-cog-outline</v-icon>
                      </v-btn>
                    </template>
                    <v-card width="417">
                      <v-list>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>{{ $t("deforestation.customization") }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider></v-divider>
                        <div class="pl-3 pt-2 font-weight-normal">
                          {{ $t("deforestation.recordsPerpage") }}
                        </div>
                        <v-list-item>
                          <v-list-item-content>
                            <v-slider
                              v-model="itemsPerPageSlider"
                              :tick-labels="itemsPerPageOptions"
                              :max="3"
                              step="1"
                              ticks="always"
                              tick-size="4"
                            ></v-slider>
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mt-n3"></v-divider>
                      </v-list>
                      <v-card-text>
                        <v-container fluid>
                          <v-row>
                            <v-col
                              v-for="header in defaultHeaders"
                              :key="header.value"
                              class="mt-n9"
                              cols="6"
                            >
                              <v-checkbox
                                v-if="header.text"
                                v-model="selectedHeaderLabels"
                                :label="header.text"
                                color="primary"
                                :value="header.text"
                                hide-details
                                :disabled="header.value === 'batchId'"
                              ></v-checkbox>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-card-text>
                      <v-layout justify-center>
                        <v-card-actions class="mb-5">
                          <v-spacer></v-spacer>
                          <v-btn outlined color="primary" width="190" @click="resetDefault">
                            <span class="text-truncate" style="max-width: 150px">
                              {{ $t("deforestation.restoreDefault") }}
                            </span>
                          </v-btn>
                          <v-btn color="primary" width="190" @click="applyTableCustomization">
                            {{ $t("deforestation.apply") }}
                          </v-btn>
                        </v-card-actions>
                      </v-layout>
                    </v-card>
                  </v-menu>
                </div>
              </div>
            </template>

            <template v-slot:footer></template>
          </v-data-table>
        </component>
      </component>
    </component>

    <ProcessingBatchAddDialog
      v-model="addDialogVisible"
      :buying-station-id="resolvedBuyingStationId"
      :used-purchase-orders="usedPurchaseOrders"
      @save="onProcessingBatchSaved"
      @close="onAddClose"
    />

    <ProcessingBatchDetailDialog
      v-model="detailDialogVisible"
      :record="viewingRecord"
      @edit="onDetailEdit"
      @processed="refresh"
      @close="onDetailClose"
    />

    <ProcessingBatchEditDialog
      v-model="editDialogVisible"
      :record="editingRecord"
      :buying-station-id="resolvedBuyingStationId"
      :used-purchase-orders="usedPurchaseOrders"
      @save="onProcessingBatchSaved"
      @close="onEditClose"
    />
  </div>
</template>

<script>
import moment from "moment";
import loadingMixin from "@/mixins/LoadingMixin";
import getPermittedActions from "@/mixins/GetPermittedActions";
import BuyingStationService from "@/_services/BuyingStationService";
import ProcessingBatchAddDialog from "./components/AddDialog.vue";
import ProcessingBatchEditDialog from "./components/EditDialog.vue";
import ProcessingBatchDetailDialog from "./components/DetailDialog.vue";

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

export default {
  name: "ProcessingBatchList",
  components: {
    ProcessingBatchAddDialog,
    ProcessingBatchEditDialog,
    ProcessingBatchDetailDialog,
  },
  mixins: [loadingMixin, getPermittedActions],
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    buyingStationId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      addDialogVisible: false,
      detailDialogVisible: false,
      viewingRecord: null,
      editDialogVisible: false,
      editingRecord: null,
      search: "",
      debounceTimer: null,
      records: [],
      loading: false,
      options: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
      fromRecord: 0,
      toRecord: 0,
      totalRecords: 0,
      totalPages: 1,
      customizeMenu: false,
      itemsPerPageOptions: ITEMS_PER_PAGE_OPTIONS,
      itemsPerPageSlider: 1,
      tableHeaders: [],
      selectedHeaderLabels: [],
      ignoreOptionsWatcherOnce: false,
    };
  },
  computed: {
    resolvedBuyingStationId() {
      return this.buyingStationId || this.$route.params.id;
    },
    breadcrumbs() {
      return [
        { text: this.$t("dashboard"), disabled: false, to: { name: "AdminDashboard" }, exact: true },
        { text: this.$t("dashboardSection.cropOverview"), disabled: true, href: "#" },
        { text: this.$t("memberDataSection.buyingStation"), disabled: false, to: { name: "CoffeeBuyingStation" }, exact: true },
        { text: this.$t("memberDataSection.processingBatch"), disabled: true, href: "#" },
      ];
    },
    defaultHeaders() {
      return [
        {
          text: this.$t("memberDataSection.batchId"),
          align: "start",
          value: "batchId",
          class: "black--text",
          sortable: true,
          width: "16%",
        },
        {
          text: this.$t("memberDataSection.lots"),
          align: "start",
          value: "lots",
          class: "black--text",
          sortable: false,
          width: "20%",
        },
        {
          text: this.$t("memberDataSection.parchmentProducedBatchRating"),
          align: "start",
          value: "greenCoffeeOut",
          class: "black--text",
          sortable: false,
          width: "26%",
        },
        {
          text: this.$t("memberDataSection.startEndDate"),
          align: "start",
          value: "startEndDate",
          class: "black--text",
          sortable: true,
          width: "20%",
        },
        {
          text: this.$t("memberDataSection.status"),
          align: "start",
          value: "status",
          class: "black--text",
          sortable: false,
          width: "12%",
        },
        { align: "center", value: "action", sortable: false, width: "72px" },
      ];
    },
    parchmentHeaderLines() {
      const label = this.$t("memberDataSection.parchmentProducedBatchRating");
      const separatorIndex = label.indexOf("&");
      if (separatorIndex > 0) {
        return {
          first: label.slice(0, separatorIndex).trim(),
          second: label.slice(separatorIndex).trim(),
        };
      }
      return { first: label, second: "" };
    },
    allHeaderLabels() {
      return this.defaultHeaders.filter((h) => h.text).map((h) => h.text);
    },
    usedPurchaseOrders() {
      const purchaseOrders = [];
      this.records.forEach((item) => {
        if (item.buyingStationOrder && item.buyingStationOrder.length > 0) {
          item.buyingStationOrder.forEach((order) => {
            if (order.orderCode) {
              purchaseOrders.push(order.orderCode);
            }
          });
        }
      });
      return [...new Set(purchaseOrders)];
    },
  },
  watch: {
    resolvedBuyingStationId() {
      this.options = { ...this.options, page: 1 };
      this.fetchList();
    },
    options: {
      handler(opt) {
        if (this.ignoreOptionsWatcherOnce) {
          this.ignoreOptionsWatcherOnce = false;
          return;
        }
        this.fetchList(opt);
      },
      deep: true,
    },
  },
  mounted() {
    this.tableHeaders = [...this.defaultHeaders];
    this.selectedHeaderLabels = [...this.allHeaderLabels];
    this.ignoreOptionsWatcherOnce = true;
    this.fetchList();
  },
  methods: {
    openAdd() {
      this.addDialogVisible = true;
    },
    openBulkImport() {
      this.$router.push({
        name: "ProcessingBatchBulkImport",
        params: { buyingStationId: String(this.resolvedBuyingStationId) },
      });
    },
    viewRecord(item) {
      if (!item) return;
      this.viewingRecord = item;
      this.detailDialogVisible = true;
    },
    onDetailClose() {
      this.viewingRecord = null;
    },
    onDetailEdit(record) {
      this.detailDialogVisible = false;
      this.viewingRecord = null;
      this.editRecord(record);
    },
    editRecord(item) {
      if (!item) return;
      this.editingRecord = {
        ...item,
        id: item.batchCode || item.id,
        batchCode: item.batchCode || item.id,
      };
      this.editDialogVisible = true;
    },
    onAddClose() {
      this.addDialogVisible = false;
    },
    onEditClose() {
      this.editingRecord = null;
      this.editDialogVisible = false;
    },
    onProcessingBatchSaved() {
      this.addDialogVisible = false;
      this.editDialogVisible = false;
      this.editingRecord = null;
      this.refresh();
    },
    async deleteRecord(item) {
      const id = item?.id;
      if (id == null) return;
      try {
        await BuyingStationService.deleteProcessBatch(id);
        this.$notify({
          type: "success",
          title: this.$t("success"),
          text: "Processing Batch Deleted successfully.",
        });
        this.refresh();
      } catch (err) {
        this.$notify({
          type: "error",
          text: err?.data?.message || err?.message || this.$t("error"),
        });
      }
    },
    formatDate(date) {
      if (!date) return "—";
      if (moment(date, "MM/DD/YYYY", true).isValid()) {
        return moment(date, "MM/DD/YYYY").format("DD/MM/YYYY");
      }
      if (moment(date).isValid()) {
        return moment(date).format("DD/MM/YYYY");
      }
      return date;
    },
    onSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.options = { ...this.options, page: 1 };
        this.fetchList();
      }, 500);
    },
    pageChange(forward) {
      const page = forward ? this.options.page + 1 : this.options.page - 1;
      this.options = { ...this.options, page };
    },
    resetDefault() {
      this.tableHeaders = [...this.defaultHeaders];
      this.selectedHeaderLabels = [...this.allHeaderLabels];
      this.itemsPerPageSlider = 1;
      this.options = { ...this.options, itemsPerPage: 10 };
      this.customizeMenu = false;
      this.fetchList();
    },
    applyTableCustomization() {
      this.tableHeaders = this.defaultHeaders.filter((header) =>
        this.selectedHeaderLabels.includes(header.text)
      );
      if (!this.tableHeaders.some((h) => h.value === "action")) {
        this.tableHeaders = [...this.tableHeaders, this.defaultHeaders[this.defaultHeaders.length - 1]];
      }
      this.options = {
        ...this.options,
        itemsPerPage: this.itemsPerPageOptions[this.itemsPerPageSlider],
        page: 1,
      };
      this.customizeMenu = false;
      this.fetchList();
    },
    refresh() {
      this.fetchList();
    },
    async fetchList(opt = null) {
      const stationId = this.resolvedBuyingStationId;
      if (!stationId) return;

      this.loading = true;
      const requestParams = {
        page: this.options.page || 1,
        itemsPerPage: this.options.itemsPerPage || 10,
        limit: this.options.itemsPerPage || 10,
        search: this.search || null,
        buyingStationId: stationId,
      };

      if (opt?.sortBy?.length) {
        requestParams.orderField = opt.sortBy[0];
        requestParams.order = opt.sortDesc[0] ? "DESC" : "ASC";
      }

      try {
        const res = await BuyingStationService.getSingleBuyingStationProcessingBatch(
          stationId,
          requestParams,
          this.search
        );

        if (res?.code === 200 && res?.data) {
          this.records = res.data.rows || [];
          this.totalRecords = res.data.totalRows || 0;
          this.totalPages = Math.ceil(this.totalRecords / this.options.itemsPerPage) || 1;
          this.fromRecord = this.totalRecords
            ? (this.options.page - 1) * this.options.itemsPerPage + 1
            : 0;
          this.toRecord = this.totalRecords
            ? Math.min(this.options.page * this.options.itemsPerPage, this.totalRecords)
            : 0;
        } else {
          this.records = [];
          this.totalRecords = 0;
          this.fromRecord = 0;
          this.toRecord = 0;
          this.totalPages = 1;
        }
      } catch (err) {
        this.records = [];
        this.totalRecords = 0;
        this.fromRecord = 0;
        this.toRecord = 0;
        this.totalPages = 1;
        this.$notify({
          type: "error",
          text: err?.data?.message || err?.message || this.$t("error"),
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.processing-batch-list-table ::v-deep thead th {
  background-color: #1b5e20 !important;
  padding: 0 20px !important;
  vertical-align: middle;
}

.processing-batch-list-table ::v-deep thead th span {
  color: #fff !important;
  text-transform: uppercase;
  font-weight: 600;
}

.processing-batch-list-table ::v-deep tbody td {
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
  padding: 12px 20px !important;
  vertical-align: top;
}

.processing-batch-list-table__two-line-header {
  display: inline-block;
  line-height: 1.3;
}

.orange-button {
  background-color: #fff7ec !important;
  border: 2px solid #ffa826 !important;
  color: #ffa826 !important;
}

.green-button {
  background-color: #d4edda !important;
  border: 2px solid #28a745 !important;
  color: #28a745 !important;
}
</style>
